import React, { useCallback, useEffect, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import * as Dialog from '@components/Dialog';
import useQueryString from '@hooks/useQueryString';
import useRequest from '@hooks/useRequest';
import { requestEnumToString } from '@utils/enumToString';
import { RequestQueryString } from '../types';
import ServiceTypeCard from './components/ServiceTypeCard';
import { ServiceTypeArray } from './serviceTypeData';
import { ServiceTypeSelectStageProps } from './types';
import CommonLayout from '@routes/Common/CommonLayout';
import FooterContainer from '@components/Footer';
import ServiceSurveyDialog from '@routes/RequestServiceType/components/ServiceSurveyDialog';
import ServiceSurveyCompleteDialog from '@routes/RequestServiceType/components/ServiceSurveyCompleteDialog';
import { ServiceSurvey } from '@components/ServiceSurvey';

const ServiceTypeSelectStage: React.FC<ServiceTypeSelectStageProps> = ({
  onInformationOpen
}) => {
  const [recallDialogOpen, setRecallDialogOpen] = useState<boolean>(false);

  const [surveyOpen, setSurveyOpen] = useState({
    progress: false,
    complete: false
  });
  const { onAddQueryString } = useQueryString<RequestQueryString>();
  const {
    container,
    titleContainer,
    cardContainer
  } = useServiceTpyeSelectTypeStyles();
  const {
    requestState,
    onInitRfqState,
    onUpdateCompleteRfq,
    hasNotEndedRequest,
    lastRequestQuery
  } = useRequest();

  const onSendRequest = useCallback(
    (requestInfo: RequestQueryString) => {
      onAddQueryString(requestInfo);
    },
    [onAddQueryString]
  );

  useEffect(() => {
    if (hasNotEndedRequest) {
      setRecallDialogOpen(true);
    }
    if (requestState.isComplete) {
      onUpdateCompleteRfq(false);
    }
  }, [requestState, onUpdateCompleteRfq, onInitRfqState, hasNotEndedRequest]);

  return (
    <CommonLayout>
      <Box className={container}>
        <ServiceSurveyDialog
          open={surveyOpen.progress}
          onCompleteOpen={() =>
            setSurveyOpen({ complete: true, progress: false })
          }
          onClose={() => setSurveyOpen({ complete: false, progress: false })}
        />
        <ServiceSurveyCompleteDialog
          open={surveyOpen.complete}
          onClose={() => setSurveyOpen({ ...surveyOpen, complete: false })}
        />
        <Dialog.ConfirmPresenter
          isOpen={recallDialogOpen}
          title={`이전에 진행하던 ${requestEnumToString(
            requestState.serviceType
          )} 요청이 남아있습니다.`}
          description="이전에 진행하던 요청을 불러올까요?"
          onOk={() => {
            setRecallDialogOpen(false);
            onSendRequest(lastRequestQuery);
          }}
          okText="불러오기"
          onClose={() => {
            onInitRfqState();
            setRecallDialogOpen(false);
          }}
          closeText="이전 요청 삭제"
        />

        <div className={titleContainer}>
          <CustomizedTypography
            variant="h1"
            fontWeight="700"
            color="textPrimary"
          >
            어떤 제조 서비스가 필요하세요?
          </CustomizedTypography>

          <CustomizedButton
            color="default"
            variant="contained"
            className="servicetype-sub-button"
            onClick={onInformationOpen}
          >
            <CustomizedTypography variant="body2" color="textPrimary">
              제조 서비스별 특징 알아보기
            </CustomizedTypography>
          </CustomizedButton>
        </div>

        <div className={cardContainer}>
          {ServiceTypeArray.map((service) => {
            return <ServiceTypeCard key={service.id} {...service} />;
          })}

          <ServiceSurvey>
            <ServiceSurvey.Card />
          </ServiceSurvey>
        </div>
      </Box>
      <FooterContainer />
    </CommonLayout>
  );
};

export default ServiceTypeSelectStage;

const useServiceTpyeSelectTypeStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '32px',
    padding: '100px 0px'
  },
  titleContainer: {
    maxWidth: 850,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& .servicetype-sub-button': {
      height: 38,
      width: 201,
      borderRadius: '50px',
      padding: '10px 19px'
    }
  },
  cardContainer: {
    width: '100%',
    maxWidth: 850,
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '16px',
    rowGap: '24px'
  }
});
